import { CalendarPlus } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Button } from '../../../components/ui/button.tsx';
import { useToast } from '../../../components/ui/use-toast.tsx';
import { useFormAutoSave } from '../../../lib/hooks/use-form-auto-save.ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { CrmNoteDTO } from '../../../server/dto/crm-note-dto.ts';
import type { CrmNoteInput } from '../../../server/dto/crm-note-input-dto.ts';
import { useAppConfig } from '../../acl/acl-provider.tsx';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { FileUploader } from '../../crm-attachment/components/file-uploader.tsx';
import { DialogForm } from '../../dialog/dialog-form.tsx';
import { NoteFormFields } from '../../notes/components/note-form-fields.tsx';

type NoteDetailsFormProps = {
    note: CrmNoteDTO;
    onUpdate?: (values: CrmNoteInput) => void;
};

export const EventNoteForm = ({ note: noteDetails, onUpdate }: NoteDetailsFormProps) => {
    const sendEvent = useAnalytics();
    const { enableFileUpload, enableCreateNextEventFakeDoor } = useAppConfig();
    const { toast } = useToast();
    const { t } = useLocale('events');

    const { control, reset, watch, formState } = useForm<CrmNoteInput>({
        mode: 'onChange',
        defaultValues: {
            title: noteDetails.titleRich,
            attachments: noteDetails.attachments,
        },
    });

    useEffect(() => {
        sendEvent('customer_interaction_viewed', {
            note_id: noteDetails.noteId,
            customer_id: noteDetails.customerId,
            note_type: noteDetails.noteType,
        });
    }, []);

    // Update form with data when it's loaded
    useEffect(() => {
        reset(
            {
                title: noteDetails.titleRich,
                attachments: noteDetails.attachments,
            },
            {
                keepValues: true,
            },
        );
    }, [noteDetails, reset]);

    // Important that we read state here, otherwise the variable will not be updated
    const isFormValid = formState.isValid;

    const handleAutoSave = (values: CrmNoteInput) => {
        if (isFormValid) {
            onUpdate?.(values);
        }
    };

    const autoSaveHandlerRef = useRef(handleAutoSave);
    autoSaveHandlerRef.current = handleAutoSave;

    // Auto-save when form values change and the form is dirty
    useFormAutoSave(
        watch,
        (values) => {
            autoSaveHandlerRef.current(values);
        },
        1000,
    );

    const handleCreateNextEventClick = () => {
        sendEvent('create_next_event_from_log', {
            customer_id: noteDetails.customerId,
            note_id: noteDetails.noteId,
        });
        toast({
            icon: 'info',
            title: t('fake_door_next_event_toast_title'),
            description: t('fake_door_next_event_toast_description'),
        });
    };

    return (
        <>
            <NoteFormFields
                noteType={noteDetails.noteType}
                noteId={noteDetails.noteId}
                control={control}
                errors={formState.errors}
            />
            {enableCreateNextEventFakeDoor && (
                <div className="self-start">
                    <Button variant="secondary" icon={CalendarPlus} onClick={handleCreateNextEventClick}>
                        {t('fake_door_next_event_button')}
                    </Button>
                </div>
            )}
            {enableFileUpload && (
                <DialogForm.ItemList className="rounded-lg bg-white py-2 px-3 shadow-xs" grid>
                    <Controller
                        name="attachments"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <FileUploader value={value ?? []} onChange={onChange} analyticsContext="note" />
                        )}
                    />
                </DialogForm.ItemList>
            )}
        </>
    );
};
